import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { apiService } from '../services/apiService';
import type { FeedStats } from '../services/apiService';

export interface NetworkProductsManagerRef {
  refresh: () => void;
}

interface Props {
  onProductsDeleted?: () => void;
}

const NetworkProductsManager = forwardRef<NetworkProductsManagerRef, Props>(({ onProductsDeleted }, ref) => {
  const [stats, setStats] = useState<FeedStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchStats = async () => {
    setError('');
    try {
      const data = await apiService.getFeedStats();
      setStats(data.stats || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useImperativeHandle(ref, () => ({ refresh: fetchStats }));

  const handleDelete = async (network: string, totalProducts: number, feedCount: number) => {
    // The endpoint (DELETE /api/affiliate-feeds/products/:network) removes the
    // network's FEEDS as well as its products — the copy here must state that
    // full scope, not just "products".
    const feedWord = feedCount === 1 ? 'feed' : 'feeds';
    const ok = window.confirm(
      `Delete ALL feeds and products for ${network}? ` +
      `This permanently removes ${feedCount} ${feedWord} and ${totalProducts} products for ${network} — ` +
      'including any URL-sync setup and EPC settings. ' +
      'Your widgets will stop matching these products. This cannot be undone.'
    );
    if (!ok) return;

    setError('');
    setNotice('');
    setDeleting(network);
    try {
      await apiService.deleteNetworkProducts(network);
      // Show our own accurate message — the endpoint's response message only
      // mentions products, but it deleted the feeds too.
      setNotice(`Removed all feeds and their products for ${network}.`);
      await fetchStats();
      onProductsDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete feeds');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return null;
  if (stats.length === 0 && !error && !notice) return null;

  return (
    <section className="feeds-section" style={{ marginTop: '2rem' }} data-testid="network-products">
      <div className="feeds-list">
      <h3>Feeds by Network</h3>
      <p className="no-data" style={{ marginTop: 0 }}>
        Remove all feeds (and their products) for one network at once.
      </p>

      {error && (
        <div data-testid="network-products-error" style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)', padding: '0.75rem', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {notice && (
        <div data-testid="network-products-notice" style={{ backgroundColor: 'var(--good-bg)', color: 'var(--good)', padding: '0.75rem', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
          {notice}
        </div>
      )}

      {stats.map((row) => (
        <div
          key={row.network}
          data-testid={`network-row-${row.network}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid var(--line)' }}
        >
          <span style={{ textTransform: 'capitalize' }}>
            <strong>{row.network}</strong> — {row.total_products} products from {row.feed_count} feed{row.feed_count === 1 ? '' : 's'}
          </span>
          <button
            data-testid={`delete-network-${row.network}`}
            onClick={() => handleDelete(row.network, row.total_products, row.feed_count)}
            disabled={deleting === row.network}
            className="btn btn--danger"
          >
            {deleting === row.network ? 'Deleting…' : `Delete all feeds for ${row.network}`}
          </button>
        </div>
      ))}
      </div>
    </section>
  );
});

NetworkProductsManager.displayName = 'NetworkProductsManager';

export default NetworkProductsManager;
